/**
 * Where the owner's "Dokument hinzufügen" dialog files a document (COL-633),
 * as pure functions.
 *
 *   Allgemein · Objektbezogen
 *               └ Objekt: one pill per unit ("Elvirastraße, Apartment 1")
 *                  └ Wohnungsbezogen · Zimmer ▾   (only while ONE unit is picked)
 *
 * It walks the same levels as the Dokumente page's drill-down (COL-628), minus
 * the tenant level: owners file against a unit or a room, never a tenancy.
 *
 * Several units can be picked at once, but only inside one building and only
 * for a unit-level filing. One building because the upload writes all its rows
 * under one property (useUploadDocumentToTargets); unit-level only because a
 * room belongs to exactly one unit.
 *
 * A building whose rooms are not assigned to any apartment (rooms.apartment_id
 * is nullable) gets one unit of its own keyed by the property, exactly as the
 * page does. It has no Wohnungsbezogen: owners cannot file on the building
 * itself, so a room is required there.
 */
import type { UploadTargetRef } from "@/hooks/v2/useApartmentDocuments";
import { byLabel } from "@/lib/owner-document-drilldown";

export type FilingUnit = {
  key: string;
  propertyId: number;
  propertyTitle: string | null;
  /** null for a building's unassigned rooms. */
  apartmentId: number | null;
  apartmentLabel: string | null;
};

export type FilingProperty = { id: number; title: string | null };
export type FilingApartment = { id: number; label: string };
export type FilingRoom = {
  id: number;
  title: string | null;
  apartment_id: number | null;
};

const apartmentKey = (id: number) => `a:${id}`;
const propertyKey = (id: number) => `p:${id}`;

/** Every unit the owner can file against, sorted like the page's units. */
export function filingUnits(
  properties: FilingProperty[],
  apartmentsByProperty: Map<number, FilingApartment[]>,
  roomsByProperty: Map<number, FilingRoom[]>
): FilingUnit[] {
  const units: FilingUnit[] = [];
  for (const p of properties) {
    for (const a of apartmentsByProperty.get(p.id) ?? []) {
      units.push({
        key: apartmentKey(a.id),
        propertyId: p.id,
        propertyTitle: p.title,
        apartmentId: a.id,
        apartmentLabel: a.label,
      });
    }
    const rooms = roomsByProperty.get(p.id) ?? [];
    if (rooms.some((r) => r.apartment_id == null)) {
      units.push({
        key: propertyKey(p.id),
        propertyId: p.id,
        propertyTitle: p.title,
        apartmentId: null,
        apartmentLabel: null,
      });
    }
  }
  return units.sort(
    (a, b) =>
      byLabel(a.propertyTitle, b.propertyTitle) ||
      byLabel(a.apartmentLabel, b.apartmentLabel)
  );
}

/** The rooms inside one unit, by title. */
export function filingRooms(
  unit: FilingUnit,
  rooms: FilingRoom[]
): FilingRoom[] {
  return rooms
    .filter((r) => (r.apartment_id ?? null) === unit.apartmentId)
    .sort((a, b) => byLabel(a.title, b.title));
}

/**
 * The selection after tapping `key`. Tapping a picked unit unpicks it.
 * Tapping a unit in another building, or a unit with no apartment (which can
 * only take a room-level file), starts a new selection with just that unit.
 */
export function toggleFilingUnit(
  selected: string[],
  key: string,
  units: FilingUnit[]
): string[] {
  if (selected.includes(key)) return selected.filter((k) => k !== key);
  const next = units.find((u) => u.key === key);
  const current = units.filter((u) => selected.includes(u.key));
  if (
    !next ||
    next.apartmentId == null ||
    current.some(
      (u) => u.apartmentId == null || u.propertyId !== next.propertyId
    )
  ) {
    return [key];
  }
  return [...selected, key];
}

/**
 * What to write, or null while the filing is incomplete.
 *
 * One unit: the unit itself, or the picked room. Several units: each of them,
 * at unit level. A unit with no apartment needs a room.
 */
export function filingTargets(
  units: FilingUnit[],
  selected: string[],
  roomId: number | null
): { propertyId: number; targets: UploadTargetRef[] } | null {
  const picked = units.filter((u) => selected.includes(u.key));
  if (picked.length === 0) return null;
  const propertyId = picked[0].propertyId;
  if (picked.some((u) => u.propertyId !== propertyId)) return null;
  if (picked.length === 1 && roomId != null) {
    return { propertyId, targets: [{ scope: "room", roomId }] };
  }
  if (picked.some((u) => u.apartmentId == null)) return null;
  return {
    propertyId,
    targets: picked.map((u) => ({
      scope: "apartment" as const,
      apartmentId: u.apartmentId!,
    })),
  };
}
